import * as React from 'react';
import { useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Button, Form, Input, InputNumber, Modal, Popconfirm, Popover, Select } from 'antd';
import { FormInstance } from 'antd/lib/form';
import {
	ClockCircleOutlined,
	DeleteOutlined,
	DownOutlined,
	EditOutlined,
	MinusCircleOutlined,
	PlusOutlined,
	UpOutlined,
	UserOutlined,
} from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';
import OWButton from '../ow_button/OWButton';
import InlineEdit from '../ow_inline_todo_list/InlineEdit';
import DeliverableAssigneeSelect from '../deliverable_assignee_select/DeliverableAssigneeSelect';
import { ProjectTaskAssignPopover } from '../project_task_assign_popover/ProjectTaskAssignPopover';
import { OWTagPill } from '../ow_tag_pill/OWTagPill';
import { UserNameWithEmail } from '../name_component/user/UserNameWithEmail';
import { withUserType } from '../withUserType';
import {
	TemplateDeliverableHeading,
	TemplateTaskLine,
	describeDays,
	getDependsOnNumber,
} from './ProjectTemplateItemDisplay';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { cn } from '../../utils/ClassNamesUtils';
import { debounce } from '../../utils/PerformanceUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { getCurrency, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import {
	buyerContactsRestCrudThunksForBuyer,
	buyerContactsRestCrudThunksForSupplier,
} from '../../thunks/buyer_contacts_thunks';

// Field blocks for a project template's deliverables and tasks. Presentational: they
// read and write the enclosing antd Form at the `name` they are given, so the task list
// serves both 'taskTemplates' and a deliverable modal's own 'tasks'.
//
// Rows carry no required rule — a blank row left behind is dropped on save by the
// clean* helpers in ProjectTemplateForm rather than blocking it. The deliverable modal
// is the exception: it requires a name, since Add is a deliberate act.
//
// A template task row looks and behaves like a real project task (DeliverableTasks
// TaskRow): title inline-editable, clock / person / delete revealed on hover, values as
// pills underneath, "+ Add task" link, and the same assignee popover. DeliverableTasks
// itself is not reusable here — it writes each edit through projectTasksRestCrudThunks,
// where a template task has no server row behind it — and a template has no calendar,
// so the clock sets a number of days rather than a date.

const { TextArea } = Input;

// A path into the enclosing form: 'taskTemplates', or ['tasks'] inside a Form.List.
type FormItemName = string | (string | number)[];

// value/onChange as antd's Form.Item injects them into a custom control.
interface FormControlProps<T> {
	value?: T;
	onChange?: (value: T) => void;
}

// A template task as the form holds it. No status or due DATE — a template has no
// calendar; daysForDue becomes a real date when a project is created from it.
interface TemplateTask {
	title?: string;
	assignTo?: string;
	daysForDue?: number;
}

interface TemplateDeliverable {
	id?: string;
	name?: string;
	description?: string;
	budget?: number;
	daysForDue?: number;
	// Plural, unlike a task's: a deliverable carries a list of emails.
	assignTo?: string[];
	dependsOnId?: string;
	tasks?: TemplateTask[];
}

interface DependencyOption {
	value: string;
	label: string;
}

// The deliverable modal's default-assignee picker. A target collection name has to
// exist in the buyer_contacts slice's standardInitialStateGenerator list.
const TEMPLATE_DELIVERABLE_ASSIGNEES_TC = 'PROJECT_TEMPLATE_CONTACTS_AUTOCOMPLETE';
// Every task row's assignee popover. Separate from the one above: both mount inside the
// deliverable modal, and a task search would otherwise truncate the select's options.
const TEMPLATE_TASK_ASSIGNEES_TC = 'PROJECT_TEMPLATE_TASK_CONTACTS_AUTOCOMPLETE';
const CONTACTS_PAGE_SIZE = 25;

const DURATION_TOOLTIP =
	'How many days this takes, counting the first day — 7 days on a project starting Oct 1 is due Oct 7. A deliverable that depends on another counts from that one’s due date instead: 14 days after a Oct 7 deliverable is due Oct 21. On a project with only an end date, counted backwards from it.';

// Width the deliverable and task lists line up to.
const ITEM_LIST_WIDTH_CLASS = 'max-w-[840px]';

// The day-count counterpart of the project task's "In 7d / In 1mo / In 3mo" presets.
const QUICK_DAYS = [
	{ label: '7d', days: 7 },
	{ label: '1mo', days: 30 },
	{ label: '3mo', days: 90 },
];

const isBlank = (value) => value === undefined || value === null || value === '';

// An icon-only row action. antd's Button forwards its ref, so it can be a Popover or
// Popconfirm trigger directly; the `!` modifiers strip .ant-btn's box (antd loads after
// Tailwind) so it sits on the text line like a bare icon.
const IconAction = React.forwardRef<HTMLElement, any>(({ label, icon, ...rest }, ref) => (
	<Button
		{...rest}
		ref={ref}
		type="text"
		size="small"
		icon={icon}
		aria-label={label}
		title={label}
		className="!h-auto !w-auto !p-0 !text-character-secondary"
	/>
));

interface TemplateItemRowProps {
	index: number;
	onRemove: () => void;
	// The row's inputs, laid out on one line with the remove control trailing them.
	controls: React.ReactNode;
	// Anything that belongs to the row but sits below its inputs (e.g. options).
	children?: React.ReactNode;
}

// The remove control trails the row's last input rather than the row's full width, so
// it reads as belonging to that row instead of floating at the far edge. Number and
// icon are boxed to the 40px input height so all three centre on the inputs.
export const TemplateItemRow: React.FC<TemplateItemRowProps> = ({
	index,
	onRemove,
	controls,
	children,
}) => (
	<div className="flex items-start gap-4">
		<div className="flex h-10 w-4 items-center text-character-secondary">{index + 1}</div>
		<div className="min-w-0 flex-1">
			<div className="flex items-start gap-4">
				{controls}
				<div className="flex h-10 items-center">
					<Popconfirm
						title="Remove this field?"
						okText="Remove"
						okButtonProps={{ danger: true }}
						cancelText="Cancel"
						onConfirm={onRemove}
					>
						<IconAction label="Remove" icon={<MinusCircleOutlined />} />
					</Popconfirm>
				</div>
			</div>
			{children}
		</div>
	</div>
);

interface TemplateItemAddButtonProps {
	label: string;
	onClick: () => void;
}

export const TemplateItemAddButton: React.FC<TemplateItemAddButtonProps> = ({ label, onClick }) => (
	<Form.Item className="mb-0">
		<Button type="dashed" onClick={onClick}>
			<PlusOutlined /> {label}
		</Button>
	</Form.Item>
);

const TemplateDurationField: React.FC<{ name: FormItemName }> = ({ name }) => (
	<Form.Item label="Takes (days)" name={name} tooltip={DURATION_TOOLTIP} className="mb-2 w-[160px]">
		<InputNumber min={0} precision={0} className="w-full" placeholder="e.g. 14" />
	</Form.Item>
);

interface TaskListFieldsProps {
	name: FormItemName;
	addLabel: string;
}

interface TemplateTaskTitleProps extends FormControlProps<string> {
	// Called when a row that never had a title is left blank — the row is discarded,
	// the way a project's blank "+ Add task" row is.
	onDiscard: () => void;
}

// A row from "Add task" has no title, so it opens straight into edit. Safe because the
// form mounts only once its data has loaded (EditPage gates on `fetching`).
const TemplateTaskTitle: React.FC<TemplateTaskTitleProps> = ({ value, onChange, onDiscard }) => {
	const [editing, setEditing] = useState(!value);

	// An emptied title on an existing task is a no-op, as on a project task.
	const commit = (next) => {
		const trimmed = (next || '').trim();
		setEditing(false);
		if (trimmed) {
			onChange(trimmed);
		} else if (!value) {
			onDiscard();
		}
	};

	const startEditing = () => setEditing(true);

	// role="button" + tabIndex make the display text reachable and activatable without a
	// mouse; it stays a div because it also has to wrap a long title.
	const handleKeyDown = (event) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			startEditing();
		}
	};

	return editing ? (
		<InlineEdit
			value={value || ''}
			placeholder="Enter task title"
			onEnter={() => {}}
			setValue={commit}
			className="bodyregular"
		/>
	) : (
		<div
			role="button"
			tabIndex={0}
			onClick={startEditing}
			onKeyDown={handleKeyDown}
			className="bodyregular min-w-0 flex-1 break-words pt-xxs text-character-primary hover:cursor-pointer"
		>
			{value}
		</div>
	);
};

// The project task's due-date popover, in days: same presets row, then the exact
// value, then Clear. Days, not a date picker — a template has no calendar of its own.
const TemplateTaskDaysPopover: React.FC<FormControlProps<number>> = ({ value, onChange }) => {
	const [open, setOpen] = useState(false);

	const pick = (next) => {
		onChange(next);
		setOpen(false);
	};

	const clearDays = () => pick(null);

	const content = (
		<div className="w-[280px]">
			<div className="footnoteregular mb-2 text-character-secondary">TAKES</div>
			<div className="mb-2 flex gap-2">
				{QUICK_DAYS.map((quick) => (
					<Button key={quick.label} size="small" onClick={() => pick(quick.days)}>
						{quick.label}
					</Button>
				))}
			</div>
			<InputNumber
				min={0}
				precision={0}
				value={value}
				placeholder="e.g. 14"
				addonAfter="days"
				className="w-full"
				onChange={onChange}
			/>
			{isBlank(value) ? null : (
				<Button type="link" size="small" className="!mt-1 !px-0" onClick={clearDays}>
					Clear time period
				</Button>
			)}
		</div>
	);

	return (
		<Popover
			trigger="click"
			visible={open}
			onVisibleChange={setOpen}
			content={content}
			placement="bottomRight"
		>
			<IconAction label="Time period" icon={<ClockCircleOutlined />} />
		</Popover>
	);
};

interface TemplateTaskAssigneePopoverProps extends FormControlProps<string> {
	// Injected by connect / withUserType.
	contacts?: any[];
	fetchContacts?: (search: string) => void;
	userType?: string;
}

// Contacts are fetched when a popover opens, not per row on mount — a template can
// carry dozens of tasks, and every row shares one collection.
const TemplateTaskAssigneePopoverComponent: React.FC<TemplateTaskAssigneePopoverProps> = ({
	value,
	onChange,
	contacts,
	fetchContacts,
}) => {
	const debouncedSearch = useRef(debounce((term) => fetchContacts(term), 400, false));

	const handleSearch = (term) => debouncedSearch.current(term);

	const handleOpenChange = (open) => {
		if (open) {
			fetchContacts('');
		}
	};

	// "No Assignee" comes back as null; the form stores no assignee as undefined.
	const handleChange = (email) => onChange(email || undefined);

	return (
		<ProjectTaskAssignPopover
			email={value}
			contacts={contacts}
			onSearch={handleSearch}
			onChange={handleChange}
			onOpenChange={handleOpenChange}
		>
			<IconAction label="Assignee" icon={<UserOutlined />} />
		</ProjectTaskAssignPopover>
	);
};

const mapAssigneeStateToProps = (state) => ({
	contacts: getRecordsForTargetCollection(state.buyer_contacts, TEMPLATE_TASK_ASSIGNEES_TC),
});

const mapAssigneeDispatchToProps = (dispatch, ownProps) => {
	const contactThunks =
		ownProps.userType === ROLE_TYPES.SUPPLIER
			? buyerContactsRestCrudThunksForSupplier
			: buyerContactsRestCrudThunksForBuyer;
	return {
		fetchContacts: (search) =>
			dispatch(
				contactThunks.read(
					{ search, limit: CONTACTS_PAGE_SIZE, offset: 0 },
					TEMPLATE_TASK_ASSIGNEES_TC
				)
			),
	};
};

const TemplateTaskAssigneePopover = withUserType(
	connect(mapAssigneeStateToProps, mapAssigneeDispatchToProps)(TemplateTaskAssigneePopoverComponent)
);

// The pills under a task title, right-aligned — the project task's TaskDetailLine with
// a time period where a project task has a due date.
const TemplateTaskDetailLine: React.FC<{ task: TemplateTask }> = ({ task }) => {
	const assignTo = nullSafeGet('assignTo', task);
	const daysForDue = nullSafeGet('daysForDue', task);
	if (!assignTo && isBlank(daysForDue)) {
		return null;
	}
	return (
		<div className="mt-xs flex flex-wrap items-center justify-end gap-xs">
			{isBlank(daysForDue) ? null : (
				<OWTagPill icon={<ClockCircleOutlined />}>{describeDays(daysForDue)}</OWTagPill>
			)}
			{assignTo ? (
				<OWTagPill className="px-2xs">
					<UserNameWithEmail
						email={assignTo}
						mode="inline"
						showNameWithAvatar
						size="small"
						placement="top"
						underline={false}
					/>
				</OWTagPill>
			) : null}
		</div>
	);
};

export const TemplateTaskListFields: React.FC<TaskListFieldsProps> = ({ name, addLabel }) => {
	// Watched only for the detail line, which needs both popover values at once.
	const form = Form.useFormInstance();
	const rows: TemplateTask[] = Form.useWatch(name, form) || [];

	return (
		<Form.List name={name}>
			{(fields, { add, remove }) => {
				const handleAddTask = () => add();
				return (
					<div className={ITEM_LIST_WIDTH_CLASS}>
						{fields.map(({ key, name: taskName, ...restField }, index) => {
							const handleRemoveTask = () => remove(taskName);
							return (
								<div key={key} className="group mb-m flex flex-row items-start gap-xs">
									{/* The row number stands where a project task has its checkbox. */}
									<div className="footnoteregular flex h-l w-4 items-center text-character-secondary">
										{index + 1}
									</div>
									<div className="min-w-0 flex-1">
										<div className="flex flex-row items-start">
											{/* antd loads after Tailwind, so its form-item margin and control
											    min-height have to be undone for the row to read as text. */}
											<Form.Item
												{...restField}
												name={[taskName, 'title']}
												className="!mb-0 min-w-0 flex-1 [&_.ant-form-item-control-input]:min-h-0"
											>
												<TemplateTaskTitle onDiscard={handleRemoveTask} />
											</Form.Item>
											{/* Faded rather than `hidden` (the project row's idiom): a
											    display:none element cannot take focus, so the actions would drop
											    out of the tab order and group-focus-within could never fire.
											    h-[22px] + mt-xxs gives them the title's first line box. */}
											<div className="pointer-events-none ml-4 mt-xxs flex h-[22px] shrink-0 flex-row items-center gap-4 opacity-0 transition-opacity duration-150 group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100">
												<Form.Item {...restField} name={[taskName, 'daysForDue']} noStyle>
													<TemplateTaskDaysPopover />
												</Form.Item>
												<Form.Item {...restField} name={[taskName, 'assignTo']} noStyle>
													<TemplateTaskAssigneePopover />
												</Form.Item>
												<Popconfirm
													placement="topRight"
													title="Delete this task?"
													okText="Delete"
													okButtonProps={{ danger: true }}
													cancelText="Cancel"
													onConfirm={handleRemoveTask}
												>
													<IconAction label="Delete task" icon={<DeleteOutlined />} />
												</Popconfirm>
											</div>
										</div>
										<TemplateTaskDetailLine task={rows[taskName] || {}} />
									</div>
								</div>
							);
						})}
						<div className="padding-less-antd-button flex flex-row pl-1">
							<Button type="link" onClick={handleAddTask}>
								<PlusOutlined /> {addLabel}
							</Button>
						</div>
					</div>
				);
			}}
		</Form.List>
	);
};

interface TemplateDeliverableModalProps {
	initialValues: TemplateDeliverable;
	currencyId: string;
	dependencyOptions: DependencyOption[];
	isNew: boolean;
	onCancel: () => void;
	onSave: (deliverable: TemplateDeliverable) => void;
}

// antd Modals portal out, so this Form is not nested in the template's <form>. Its
// values are handed back on save, which is what makes Cancel a no-op.
const TemplateDeliverableModal: React.FC<TemplateDeliverableModalProps> = ({
	initialValues,
	currencyId,
	dependencyOptions,
	isNew,
	onCancel,
	onSave,
}) => {
	const [form] = Form.useForm();

	const handleSave = () => {
		// The id and anything the modal does not edit ride along from the row as it was.
		form.validateFields().then((values) => onSave({ ...initialValues, ...values }));
	};

	return (
		<Modal
			visible={true}
			title={isNew ? 'Add deliverable' : 'Edit deliverable'}
			onCancel={onCancel}
			width={720}
			destroyOnClose={true}
			footer={[
				<Button key="cancel" onClick={onCancel}>
					Cancel
				</Button>,
				<OWButton key="save" type="primary" onClick={handleSave}>
					{isNew ? 'Add deliverable' : 'Save deliverable'}
				</OWButton>,
			]}
		>
			<Form form={form} layout="vertical" initialValues={initialValues} preserve={false}>
				<Form.Item
					label="Name"
					name="name"
					rules={[{ required: true, message: 'Give this deliverable a name' }]}
				>
					<Input />
				</Form.Item>
				<Form.Item label="Description" name="description">
					<TextArea autoSize={{ minRows: 2 }} />
				</Form.Item>
				<div className="flex items-start gap-4">
					<Form.Item label="Budget" name="budget" className="mb-2 min-w-0 flex-1">
						<InputNumber min={0} className="flex w-full" addonAfter={currencyId} />
					</Form.Item>
					<TemplateDurationField name="daysForDue" />
				</div>
				<Form.Item
					label="Default assignees"
					name="assignTo"
					tooltip="Everyone here is assigned to this deliverable when a project is created from the template. Assignees can still be changed on the project."
				>
					<DeliverableAssigneeSelect
						targetCollectionName={TEMPLATE_DELIVERABLE_ASSIGNEES_TC}
						placeholder="Nobody by default"
					/>
				</Form.Item>
				<Form.Item
					label="Depends on"
					name="dependsOnId"
					tooltip="The deliverable that has to finish first. This one's days then count from that deliverable's due date rather than from the project start, and the dependency carries onto the project so a later date change cascades."
				>
					<Select
						allowClear
						showSearch
						optionFilterProp="label"
						placeholder="No dependency"
						options={dependencyOptions}
					/>
				</Form.Item>
				<div className="bodymedium mb-2">Tasks</div>
				<TemplateTaskListFields name="tasks" addLabel="Add task" />
			</Form>
		</Modal>
	);
};

interface DeliverableSummaryRowProps {
	index: number;
	deliverable: TemplateDeliverable;
	currency: any;
	dependsOnNumber: number | null;
	dependsOnName: string | null;
	onEdit: () => void;
	onRemove: () => void;
}

// One deliverable in the form's list: the same heading the detail page shows, with
// edit / delete trailing it and its tasks behind an "N tasks" toggle, numbered 1.1, 1.2.
const DeliverableSummaryRow: React.FC<DeliverableSummaryRowProps> = ({
	index,
	deliverable,
	currency,
	dependsOnNumber,
	dependsOnName,
	onEdit,
	onRemove,
}) => {
	const [tasksOpen, setTasksOpen] = useState(false);
	const tasks: TemplateTask[] = nullSafeGetOrElse('tasks', deliverable, []);
	const number = index + 1;

	const toggleTasks = () => setTasksOpen(!tasksOpen);

	const actions = (
		<div className="flex shrink-0 items-center gap-m">
			<IconAction label="Edit deliverable" icon={<EditOutlined />} onClick={onEdit} />
			<Popconfirm
				placement="topRight"
				title="Delete this deliverable?"
				okText="Delete"
				okButtonProps={{ danger: true }}
				cancelText="Cancel"
				onConfirm={onRemove}
			>
				<IconAction label="Delete deliverable" icon={<DeleteOutlined />} />
			</Popconfirm>
		</div>
	);

	return (
		<div
			className={cn('px-l py-m', {
				'border-0 border-t border-solid border-owgray': index > 0,
			})}
		>
			<TemplateDeliverableHeading
				number={number}
				deliverable={deliverable}
				dependsOnNumber={dependsOnNumber}
				dependsOnName={dependsOnName}
				currency={currency}
				actions={actions}
			/>
			{tasks.length > 0 ? (
				<Button
					type="link"
					className="!mt-2xs !h-auto !p-0 !text-character-secondary"
					onClick={toggleTasks}
					aria-expanded={tasksOpen}
				>
					<span className="underline">{`${tasks.length} ${
						tasks.length === 1 ? 'task' : 'tasks'
					}`}</span>
					{tasksOpen ? <UpOutlined /> : <DownOutlined />}
				</Button>
			) : null}
			{tasksOpen ? (
				<div className="mt-xs">
					{tasks.map((task, taskIndex) => (
						<TemplateTaskLine
							key={taskIndex}
							task={task}
							number={`${number}.${taskIndex + 1}`}
							className="py-xs"
						/>
					))}
				</div>
			) : null}
		</div>
	);
};

interface DeliverableListFieldsProps {
	name: string;
	// Currency code shown on the budget input, e.g. 'USD'.
	currencyId: string;
	form: FormInstance;
}

// Which row the modal is editing: its position, or null for one that does not exist
// yet, alongside the values it opened with.
interface EditingDeliverable {
	index: number | null;
	values: TemplateDeliverable;
}

export const TemplateDeliverableListFields: React.FC<DeliverableListFieldsProps> = ({
	name,
	currencyId,
	form,
}) => {
	const rows: TemplateDeliverable[] = Form.useWatch(name, form) || [];
	const currency = getCurrency({ currencyId });
	const [editing, setEditing] = useState<EditingDeliverable>(null);

	// Siblings by stable id, minus itself — the one dependency the API is sure to reject.
	const dependencyOptions = (currentId): DependencyOption[] =>
		rows
			.map((row, index) => ({
				value: nullSafeGetOrElse('id', row, ''),
				label: `${index + 1}. ${nullSafeGetOrElse('name', row, 'Untitled deliverable')}`,
			}))
			.filter((option) => option.value && option.value !== currentId);

	const dependsOnName = (dependsOnNumber) =>
		dependsOnNumber
			? nullSafeGetOrElse('name', rows[dependsOnNumber - 1], 'Untitled deliverable')
			: null;

	// Form.List has add and remove but no update. Length is unchanged, so keys survive.
	const replaceRow = (index, values) => {
		const current = form.getFieldValue(name) || [];
		form.setFieldsValue({
			[name]: current.map((row, rowIndex) => (rowIndex === index ? values : row)),
		});
	};

	const closeModal = () => setEditing(null);

	return (
		<Form.List name={name}>
			{(fields, { add, remove }) => {
				// The id is minted once here and travels with the row for its lifetime.
				const handleAddDeliverable = () =>
					setEditing({ index: null, values: { id: uuidv4(), tasks: [], assignTo: [] } });

				const handleModalSave = (values) => {
					if (editing.index === null) {
						add(values);
					} else {
						replaceRow(editing.index, values);
					}
					closeModal();
				};

				return (
					<>
						{fields.length > 0 ? (
							<div className={cn('mb-m border border-solid border-owgray', ITEM_LIST_WIDTH_CLASS)}>
								{fields.map(({ key, name: deliverableName }, index) => {
									// Indexed: a nullSafeGet path splits on '.', so a dynamic key mis-resolves.
									const deliverable = rows[deliverableName] || {};
									const dependsOnNumber = getDependsOnNumber(deliverable, rows);
									const handleEdit = () =>
										setEditing({ index: deliverableName, values: deliverable });
									const handleRemove = () => remove(deliverableName);
									return (
										<DeliverableSummaryRow
											key={key}
											index={index}
											deliverable={deliverable}
											currency={currency}
											dependsOnNumber={dependsOnNumber}
											dependsOnName={dependsOnName(dependsOnNumber)}
											onEdit={handleEdit}
											onRemove={handleRemove}
										/>
									);
								})}
							</div>
						) : null}
						<Button icon={<PlusOutlined />} onClick={handleAddDeliverable}>
							Add deliverable
						</Button>
						{editing ? (
							<TemplateDeliverableModal
								initialValues={editing.values}
								currencyId={currencyId}
								dependencyOptions={dependencyOptions(nullSafeGetOrElse('id', editing.values, ''))}
								isNew={editing.index === null}
								onCancel={closeModal}
								onSave={handleModalSave}
							/>
						) : null}
					</>
				);
			}}
		</Form.List>
	);
};
